import { Button, Card } from '@design-system';
import {
  OnboardingLayout,
  useSlideSetup,
} from '@/features/client-onboarding';
import { OnboardingInsightsSummary } from '@/pages/OnboardingInsightsSummary';
import { D2CCheckoutPage } from '@/pages/D2CCheckoutPage';
import { useNavigateToPage } from '@/Router';
import { Star, Eye, Heart, TrendingUp } from 'lucide-react';

const VALUE_PROPS = [
  {
    icon: <Eye className="text-primary size-5" />,
    title: 'Understand what care may cost',
    description: 'Fit your plan to your needs',
  },
  {
    icon: <Heart className="text-primary size-5" />,
    title: 'How to reduce stress on loved ones',
    description: 'Comprehensive care planning and family involvement predictions',
  },
  {
    icon: <TrendingUp className="text-primary size-5" />,
    title: 'Access funding strategies',
    description: 'Analyze any assets, income, or insurance policy and model how it fits your plan',
  },
];

export function OnboardingD2CPaywall({ clientId }: ClientIdProps) {
  const navigateToPage = useNavigateToPage();

  useSlideSetup({
    slidePage: OnboardingD2CPaywall,
    nextButtonLabel: 'Next',
    previousSlidePage: OnboardingInsightsSummary,
    shouldHideNextNavigation: true,
  });

  const handleAccessClick = () => {
    navigateToPage(D2CCheckoutPage, { clientId });
  };

  return (
    <OnboardingLayout>
      <div className="mx-auto flex w-full max-w-4xl flex-col items-center py-6 md:py-16">
        <h1 className="text-balance text-center text-2xl font-bold text-gray-950 md:text-4xl">
          Turn your predictions into a real long-term care plan
        </h1>
        <p className="text-foreground mt-3 text-balance text-center text-base md:text-lg">
          See what your care will likely cost, build a realistic care plan, and compare ways to fund
          it.
        </p>

        <div className="mt-12 grid w-full gap-8 md:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold text-gray-950 md:text-2xl">
              Why families go deeper with Waterlily
            </h2>
            <div className="mt-6 space-y-6">
              {VALUE_PROPS.map(prop => (
                <div
                  key={prop.title}
                  className="flex gap-3"
                >
                  <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
                    {prop.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-950">{prop.title}</h3>
                    <p className="text-foreground mt-0.5 text-sm">{prop.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Card className="flex flex-col gap-4 p-6">
            <p className="text-primary text-sm font-medium">Don&apos;t stop now</p>
            <h3 className="text-xl font-bold text-gray-950">Finish your LTC plan</h3>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="size-4 fill-amber-400 text-amber-400"
                  />
                ))}
              </div>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                Trusted by thousands of families and hundreds of financial professionals
              </p>
              <p className="text-foreground mt-3 text-sm italic leading-relaxed">
                &ldquo;We highly recommend their services for planning your long term health care
                needs. Thank you Lily and Waterlily staff for your time and sincere commitment in
                helping families like ours.&rdquo;
              </p>
              <p className="mt-2 text-sm font-medium text-gray-950">-- George Tabora</p>
            </div>

            <Button
              size="lg"
              className="mt-2 w-full rounded-full"
              onClick={handleAccessClick}
            >
              Access Full Planning Now
            </Button>
            <p className="text-foreground text-center text-xs">
              $19 for full access for 1 year. Cancel anytime.
            </p>
          </Card>
        </div>
      </div>
    </OnboardingLayout>
  );
}
